import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap, catchError, throwError, OperatorFunction } from 'rxjs';
import { enviroment } from 'src/environments/environment';
import { Account } from '../model/form/account';
import {
  LoginResponse,
  CognitoApiResponse,
  GetUserResponse,
} from '../model/response/auth-response';
import { AccountSharingAlertService } from './account-sharing-alert.service';
import { AccountDeletionService } from './account-deletion.service';

/**
 * Exceções Cognito que a UI sabe tratar (mapeadas no enum CognitoException).
 * Só desembrulhamos um erro interno do UserLambdaValidationException para um
 * desses; tipos desconhecidos mantêm o nome do wrapper para não vazar erro cru.
 */
const KNOWN_COGNITO_EXCEPTIONS = [
  'NotAuthorizedException',
  'UserNotConfirmedException',
  'UsernameExistsException',
  'InvalidPasswordException',
  'CodeMismatchException',
  'ExpiredCodeException',
  'UserNotFoundException',
  'LimitExceededException',
  'NetworkingError',
];

@Injectable({
  providedIn: 'root'
})
export class CognitoService {

  private globalAuthUrl = enviroment.BASE_URL_API + '/global';
  private authUrl = enviroment.BASE_URL_API + '/auth';
  private cognitoUrl = enviroment.BASE_URL_API + '/user';
  private httpOptions = {};

  /**
   * HttpClient sem interceptor, usado só na troca do handoff. Ver exchangeHandoffCode().
   */
  private readonly httpNoIntercept: HttpClient;

  constructor(
    private http: HttpClient,
    httpBackend: HttpBackend,
    private accountSharingAlertService: AccountSharingAlertService,
    private accountDeletionService: AccountDeletionService,
  ) {
    this.httpNoIntercept = new HttpClient(httpBackend);
  }

  // ========================= Cadastro =========================

  signUp(account: Account): Observable<CognitoApiResponse> {
    return this.http.post<CognitoApiResponse>(`${this.globalAuthUrl}/signup`, {
      email: account.email,
      password: account.password,
      name: account.name,
      country: account.country.id.toString(),
      docNumber: account.docNumber,
      docType: account.docType.name.toLowerCase(),
      birthDate: account.birthDate ? account.birthDate.toISOString().substring(0, 10) : '',
      phone: account.phone,
      dialCode: account.phoneCountry.dialCode.replace('+', ''),
      phoneCountry: account.phoneCountry.id.toString(),
      cookies: new Boolean(account.checkCookies).toString(),
      newsletter: new Boolean(account.checkNewsletter).toString(),
      language: account.language
    }).pipe(this.mapApiError());
  }

  confirmSignup(email: string, code: string): Observable<CognitoApiResponse> {
    return this.http.post<CognitoApiResponse>(`${this.globalAuthUrl}/confirm-signup`, {
      email,
      code
    }).pipe(this.mapApiError());
  }

  resendConfirmationCode(email: string): Observable<CognitoApiResponse> {
    return this.http.post<CognitoApiResponse>(`${this.globalAuthUrl}/resend-code`, {
      email
    }).pipe(this.mapApiError());
  }

  // ========================= Login =========================

  login(email: string, password: string): Observable<LoginResponse> {
    const headers = new HttpHeaders({ 'X-Client-Platform': 'web' });
    return this.http.post<LoginResponse>(`${this.globalAuthUrl}/login`, {
      email,
      password
    }, { headers }).pipe(
      tap((response) => this.storeTokens(response)),
      this.mapApiError()
    );
  }

  /**
   * Troca o código de uso único do handoff do desktop (Vision) pelos tokens da sessão.
   * Endpoint sem autenticação — o código é a credencial. Ver
   * ftcloud-api/docs/integracoes/HANDOFF_DESKTOP_WEB_ONETIME_CODE.md.
   *
   * Usa httpNoIntercept (HttpBackend) de propósito. O path é /global/*, que casa com o
   * shouldAddHeaders do HttpRequestInterceptor — passando pelo interceptor, esta chamada
   * levaria o Authorization e o X-Session-Id de uma sessão anterior, sendo que o contrato
   * a define como anônima. Pior: um X-Session-Id morto faria a API responder
   * 401 SESSION_EXPIRED, e o interceptor reage a isso chamando logout() — que apagaria a
   * sessão no meio do handoff e mandaria o usuário pro /login.
   *
   * Ao contrário do login, NÃO grava os tokens: quem chama precisa decidir antes se a
   * sessão já aberta no navegador é de outro usuário e deve ser descartada — ver
   * AuthService.consumeHandoffCode(). Gravar aqui atropelaria essa decisão.
   *
   * Sem mapApiError(): a resposta de erro é o `{ error: 'invalid_or_expired_code' }` da nossa
   * API, não um __type do Cognito, e o chamador só precisa saber se funcionou.
   */
  exchangeHandoffCode(code: string): Observable<LoginResponse> {
    const headers = new HttpHeaders({
      'X-Client-Platform': 'web',
      'Content-Type': 'application/json; charset=utf-8',
    });
    return this.httpNoIntercept.post<LoginResponse>(
      `${this.globalAuthUrl}/handoff/exchange`,
      { code },
      { headers },
    );
  }

  /**
   * Grava os tokens de uma resposta de autenticação. Exposto para o handoff, que precisa
   * gravar em um momento próprio (depois de resolver a sessão anterior).
   */
  applyAuthenticationResult(response: LoginResponse): void {
    this.storeTokens(response);
  }

  respondToChallenge(session: string, username: string, code: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.globalAuthUrl}/respond-challenge`, {
      session,
      username,
      code
    }).pipe(
      tap((response) => this.storeTokens(response)),
      this.mapApiError()
    );
  }

  refreshAuth(refreshToken: string, usernameAWS: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.globalAuthUrl}/refresh`, {
      refreshToken,
      usernameAWS
    }).pipe(
      tap((response) => {
        if (response.authenticationResult) {
          if (response.authenticationResult.accessToken) {
            sessionStorage.setItem('token', response.authenticationResult.accessToken);
          }
          if (response.authenticationResult.idToken) {
            sessionStorage.setItem('idToken', response.authenticationResult.idToken);
          }
        }
        // A API mantém a mesma sessão no refresh (desde que X-Session-Id seja
        // enviado), mas pode devolver uma diferente se a anterior tiver expirado
        // ou sido derrubada por outro dispositivo — sempre substitui pela atual.
        if (response.sessionId) {
          sessionStorage.setItem('sessionId', response.sessionId);
        }
      }),
      this.mapApiError()
    );
  }

  // ========================= Senha =========================

  forgotPassword(email: string): Observable<CognitoApiResponse> {
    return this.http.post<CognitoApiResponse>(`${this.globalAuthUrl}/forgot-password`, {
      email
    }).pipe(this.mapApiError());
  }

  confirmForgotPassword(email: string, password: string, code: string): Observable<CognitoApiResponse> {
    return this.http.post<CognitoApiResponse>(`${this.globalAuthUrl}/confirm-forgot-password`, {
      email,
      password,
      code
    }).pipe(this.mapApiError());
  }

  changePassword(oldPassword: string, newPassword: string): Observable<CognitoApiResponse> {
    const accessToken = sessionStorage.getItem('token') || '';
    return this.http.post<CognitoApiResponse>(`${this.globalAuthUrl}/change-password`, {
      accessToken,
      oldPassword,
      newPassword
    }).pipe(this.mapApiError());
  }

  changeNewRequiredPass(session: string, username: string, newPassword: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.globalAuthUrl}/new-password-required`, {
      session,
      username,
      newPassword
    }).pipe(
      tap((response) => this.storeTokens(response)),
      this.mapApiError()
    );
  }

  // ========================= Usuário =========================

  getUser(token: string): Observable<GetUserResponse> {
    return this.http.get<GetUserResponse>(`${this.globalAuthUrl}/user`, {
      params: { accessToken: token }
    }).pipe(this.mapApiError());
  }

  setUserMFA(mfaEnabled: boolean): Observable<CognitoApiResponse> {
    const accessToken = sessionStorage.getItem('token') || '';
    return this.http.post<CognitoApiResponse>(`${this.globalAuthUrl}/mfa`, {
      accessToken,
      mfaEnabled
    }).pipe(this.mapApiError());
  }

  // ========================= Logout =========================

  revokeAuth(): Observable<CognitoApiResponse> {
    const refreshToken = sessionStorage.getItem('refreshToken') || '';
    return this.http.post<CognitoApiResponse>(`${this.globalAuthUrl}/revoke`, {
      refreshToken
    }).pipe(this.mapApiError());
  }

  // ========================= Admin (sem mudança) =========================

  getCognitoUsername(email: string): Observable<any> {
    const url = this.cognitoUrl + `/adminGetUsernameUser/${email}`;
    return this.http.get(url, { ...this.httpOptions, responseType: 'text' as 'text' }).pipe(tap());
  }

  resetUserPassword(username: string): Observable<any> {
    const url = this.cognitoUrl + `/adminResetUserPassword/${username}`;
    return this.http.post(url, this.httpOptions).pipe(tap());
  }

  // ========================= Helpers =========================

  private storeTokens(response: LoginResponse): void {
    const authResult = response.authenticationResult;
    if (authResult) {
      if (authResult.refreshToken) {
        sessionStorage.setItem('refreshToken', authResult.refreshToken);
      }
      if (authResult.idToken) {
        sessionStorage.setItem('idToken', authResult.idToken);
      }
      if (authResult.accessToken) {
        sessionStorage.setItem('token', authResult.accessToken);
      }
    }
    // Login/MFA/nova senha obrigatória: a API cria a sessão de dispositivo aqui.
    // Ver docs/integracoes/frontend-angular/ANGULAR_SESSION_ID_CONTROLE_DISPOSITIVOS.md (ftcloud-api).
    if (response.sessionId) {
      sessionStorage.setItem('sessionId', response.sessionId);
    }
    // Quem exibe o aviso é o shell do dashboard, não a tela de login — por isso fica
    // guardado até lá. Ausente (o caso normal) não grava nada.
    this.accountSharingAlertService.store(response.accountSharingAlert);
    // Conta em carência de exclusão: quem roteia (login/MFA/handoff/guard) lê o flag daqui.
    this.accountDeletionService.applyLoginResponse(response);
  }

  /**
   * Mapeia erros da API para o formato esperado pelos componentes.
   * Extrai o __type do Cognito do campo details para manter compatibilidade
   * com CognitoException enum usado nos componentes.
   */
  private mapApiError<T>(): OperatorFunction<T, T> {
    return catchError<T, Observable<never>>((httpError: any) => {
      const details = httpError.error?.details || '';
      let errorName = 'UnknownError';
      let errorMessage = '';

      try {
        const parsed = JSON.parse(details);
        errorName = parsed.__type || parsed.error || 'UnknownError';
        errorMessage = parsed.message || details;
        if (errorName === 'UserLambdaValidationException' && parsed.message) {
          const inner = parsed.message.match(/"errorType"\s*:\s*"([^"]+)"/);
          if (inner && KNOWN_COGNITO_EXCEPTIONS.includes(inner[1])) {
            errorName = inner[1];
          }
        }
      } catch {
        errorMessage = details || httpError.message || 'Erro desconhecido';
        // Tentar extrair tipo de erro da string
        if (details.includes('NotAuthorizedException')) errorName = 'NotAuthorizedException';
        else if (details.includes('UserNotConfirmedException')) errorName = 'UserNotConfirmedException';
        else if (details.includes('UsernameExistsException')) errorName = 'UsernameExistsException';
        else if (details.includes('InvalidPasswordException')) errorName = 'InvalidPasswordException';
        else if (details.includes('CodeMismatchException')) errorName = 'CodeMismatchException';
        else if (details.includes('ExpiredCodeException')) errorName = 'ExpiredCodeException';
        else if (details.includes('UserNotFoundException')) errorName = 'UserNotFoundException';
        else if (details.includes('UserLambdaValidationException')) errorName = 'UserLambdaValidationException';
        else if (details.includes('LimitExceededException')) errorName = 'LimitExceededException';
        else if (details.includes('NetworkingError')) errorName = 'NetworkingError';
      }

      const error = new Error(errorMessage);
      (error as any).name = errorName;
      return throwError(() => error);
    });
  }
}
